import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useStores } from '~/hooks';
import { IntercomContext, IntercomSettings } from '~/contexts';
import { EMPTY_FN } from '~/constants';
import { assign, formatUrlSuffix, inIframe } from '~/utils';
import initIntercom from './init';

const {
    app_id: APP_ID,
    api_base: API_BASE,
    sdk_url: SDK_URL
} = window.jdy_config.customer_service || {};

interface IntercomProviderProps {
    bootConfig?: IntercomSettings;
    /** 使 Launcher 和 Messenger 同步显示 */
    syncLauncherAndMessenger?: boolean;
    /** 当前场景是否可加载Intercom */
    loadable?: boolean;
}

/**
 * Intercom相关配置和API
 * @see https://developers.intercom.com/installing-intercom/web/methods
 * @param props IntercomProviderProps
 */
const IntercomProvider: React.FC<PropsWithChildren<IntercomProviderProps>> = (props) => {
    const { bootConfig, syncLauncherAndMessenger = true, loadable = true, children } = props;

    const { corpUserStore } = useStores();
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    /**
     * 以下场景不加载intercom
     * 1、在iframe中
     * 2、外链
     * 3、没有应用创建权限（子管理员及以上权限）
     */
    const needBlocked = useMemo(() => {
        const needBlocked = inIframe() || !loadable || !APP_ID || !SDK_URL;

        if (corpUserStore.isUserExist) {
            return needBlocked || !corpUserStore.hasPlatformManager;
        }

        return needBlocked;
    }, []);

    useEffect(() => {
        if (needBlocked) {
            return;
        }

        initIntercom(formatUrlSuffix(SDK_URL!, APP_ID!)).then(() => {
            boot();
            setIsInitialized(true);
        });

        return () => {
            shutdown();
        };
    }, []);

    const boot = () => {
        if (!window.Intercom) {
            return;
        }

        if (syncLauncherAndMessenger) {
            onMessengerHide(() => {
                hideLauncher();
            });

            onMessengerShow(() => {
                showLauncher();
            });
        }

        // 设置 Intercom 初始配置
        const initSetting = {
            api_base: API_BASE,
            app_id: APP_ID,
            ...bootConfig
        };

        if (corpUserStore.isUserExist) {
            assign(initSetting, {
                email: corpUserStore.userEmail,
                user_id: corpUserStore.username,
                created_at: corpUserStore.userRegTime,
                name: corpUserStore.userNickname,
                phone: corpUserStore.userPhone,
                user_hash: corpUserStore.customerServiceUserHash,
                avatar: {
                    type: 'avatar',
                    image_url: corpUserStore.memberAvatar || ''
                },
                company: {
                    company_id: corpUserStore.tenantId,
                    name: corpUserStore.currentCorpName,
                    plan: corpUserStore.suiteVipLevel,
                    size: corpUserStore.corpSize
                },
                ID: corpUserStore.userId,
                'Is Workspace Owner': corpUserStore.isCorpOwner,
                'Is Workspace Admin': corpUserStore.hasSysAdmin,
                'Is Workspace Sub Admin': corpUserStore.hasPlatformManager
            });
        }
        window.intercomSettings = initSetting;
        window.Intercom?.('boot', initSetting);
    };

    const shutdown = () => {
        window.Intercom?.('shutdown');
        setIsInitialized(false);
        delete window.intercomSettings;
    };

    const updateSettings = (settings: IntercomSettings) => {
        window.Intercom?.('update', settings);
    };

    const showMessenger = () => {
        window.Intercom?.('show');
    };

    /**
     * 当Intercom Messenger面板隐藏时触发该回调
     * 注意：该回调可以重复绑定，且不会覆盖之前的回调，且会按绑定的先后顺序依次执行
     * @param callback 回调函数
     */
    const onMessengerHide = (callback = EMPTY_FN) => {
        window.Intercom?.('onHide', callback);
    };

    /**
     * 当Intercom Messenger面板显示时触发该回调
     * 注意：该回调可以重复绑定，且不会覆盖之前的回调，且会按绑定的先后顺序依次执行
     * @param callback 回调函数
     */
    const onMessengerShow = (callback = EMPTY_FN) => {
        window.Intercom?.('onShow', callback);
    };

    const hideLauncher = () => {
        updateSettings({ hide_default_launcher: true });
    };

    const showLauncher = () => {
        updateSettings({ hide_default_launcher: false });
    };

    return (
        <IntercomContext.Provider
            value={{
                isInitialized,
                showMessenger,
                onMessengerHide
            }}
        >
            {children}
        </IntercomContext.Provider>
    );
};

export default IntercomProvider;
